import React, {
  type ClipboardEventHandler,
  type KeyboardEventHandler,
  type ReactNode,
  type InputHTMLAttributes,
} from 'react';
import { FieldError } from './FieldError';
import { cn } from '~/utils';

type InputProps = {
  placeholder?: string;
  name: string;
  value: string | number;
  onChange?: (name: string, value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  rightAlign?: boolean;
  onPaste?: ClipboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >;
  dataCy?: string;
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  | 'value'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'className'
  | 'onPaste'
  | 'onKeyDown'
  | 'placeholder'
  | 'name'
  | 'disabled'
  | 'id'
  | 'required'
  | 'aria-describedby'
  | 'aria-disabled'
  | 'aria-invalid'
  | 'aria-required'
  | 'tabIndex'
>;

export type InputLabel = ReactNode | React.ComponentType;

type InputWrapperProps = {
  label?: InputLabel;
  id?: string;
  errorId?: string;
  error?: string;
  required?: boolean;
  renderRequiredSymbol?: boolean;
  disabled?: boolean;
  containerClassName?: string;
  focus?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children?: ReactNode;
};

export type BaseInputProps = InputProps & InputWrapperProps;

export const InputWrapper = ({
  label,
  id,
  errorId,
  error,
  required,
  renderRequiredSymbol = required,
  disabled,
  containerClassName,
  focus,
  leftIcon,
  rightIcon,
  children,
}: InputWrapperProps) => {
  const hasError = error !== undefined && error !== '';
  const renderedLabel =
    typeof label === 'function' ? React.createElement(label) : label;

  return (
    <div className={cn('flex flex-col w-full gap-2')}>
      {renderedLabel !== undefined && (
        <label
          htmlFor={id}
          className="ml-md mb-xxs text-sm font-medium text-neut-prim"
        >
          {renderedLabel}
          {required && renderRequiredSymbol ? (
            <span className="text-status-error"> *</span>
          ) : (
            ''
          )}
        </label>
      )}

      <div
        className={cn(
          'border-1 rounded-md hover:border-prim hover:ring-prim/50 hover:ring-[3px]',
          'rounded-xxs flex flex-row justify-between items-center transition-shadow',
          hasError ? 'border-error-500' : 'border-neut-border',
          focus ? 'border-prim ring-prim/50 ring-[3px]' : '',
          !focus && !disabled ? 'hover-bg-black-11-5' : '',
          containerClassName
        )}
      >
        {leftIcon ? <div className="ml-3">{leftIcon}</div> : null}
        {children}
        {rightIcon ? <div className="mx-3">{rightIcon}</div> : null}
      </div>

      {hasError && <FieldError id={errorId}>{error}</FieldError>}
    </div>
  );
};
